/**
 * Job Activity Timeline Configuration
 * Defines activity types, icons, colors, and labels for the job activity timeline
 */

export const JOB_ACTIVITY_TYPES = {
  JOB_CREATED: 'job_created',
  JOB_UPDATED: 'job_updated',
  STATUS_CHANGED: 'status_changed',
  JOB_ARCHIVED: 'job_archived',
  RECRUITER_ASSIGNED: 'recruiter_assigned',
  RECRUITER_REMOVED: 'recruiter_removed',
  APPLICATION_ADDED: 'application_added',
  APPLICATION_REMOVED: 'application_removed'
} as const;

export type JobActivityType = typeof JOB_ACTIVITY_TYPES[keyof typeof JOB_ACTIVITY_TYPES];

export interface ActivityConfig {
  icon: string;
  color: string;
  label: string;
  cssClass: string;
}

export const JOB_ACTIVITY_CONFIG: Record<JobActivityType, ActivityConfig> = {
  [JOB_ACTIVITY_TYPES.JOB_CREATED]: {
    icon: 'Plus',
    color: '#10b981',
    label: 'Job Created',
    cssClass: 'activity-created'
  },
  [JOB_ACTIVITY_TYPES.JOB_UPDATED]: {
    icon: 'Edit',
    color: '#3b82f6',
    label: 'Job Updated',
    cssClass: 'activity-updated'
  },
  [JOB_ACTIVITY_TYPES.STATUS_CHANGED]: {
    icon: 'ArrowRight',
    color: '#6366f1',
    label: 'Status Changed',
    cssClass: 'activity-status'
  },
  [JOB_ACTIVITY_TYPES.JOB_ARCHIVED]: {
    icon: 'Archive',
    color: '#6b7280',
    label: 'Job Archived',
    cssClass: 'activity-archived'
  },
  [JOB_ACTIVITY_TYPES.RECRUITER_ASSIGNED]: {
    icon: 'UserPlus',
    color: '#8b5cf6',
    label: 'Recruiter Assigned',
    cssClass: 'activity-recruiter-add'
  },
  [JOB_ACTIVITY_TYPES.RECRUITER_REMOVED]: {
    icon: 'UserMinus',
    color: '#f59e0b',
    label: 'Recruiter Removed',
    cssClass: 'activity-recruiter-remove'
  },
  [JOB_ACTIVITY_TYPES.APPLICATION_ADDED]: {
    icon: 'UserCheck',
    color: '#14b8a6',
    label: 'Candidate Applied',
    cssClass: 'activity-application-add'
  },
  [JOB_ACTIVITY_TYPES.APPLICATION_REMOVED]: {
    icon: 'UserX',
    color: '#ef4444',
    label: 'Application Removed',
    cssClass: 'activity-application-remove'
  }
};

export interface FieldChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface RelatedEntity {
  type: string;
  id: number;
  name: string;
}

export interface JobActivityDetails {
  fieldChanges?: FieldChange[];
  relatedEntity?: RelatedEntity;
  fromStatus?: string;
  toStatus?: string;
  archiveReason?: string;
  archiveReasonNote?: string;
  note?: string;
}

export interface JobActivityItem {
  id: string;
  type: JobActivityType;
  timestamp: string;
  user: {
    id: number;
    name: string;
  };
  description: string;
  details: JobActivityDetails;
}

export interface JobActivityHistoryResponse {
  success: boolean;
  data: {
    activities: JobActivityItem[];
    total: number;
    hasMore: boolean;
  };
  message: string;
}

export interface JobActivitySummaryResponse {
  success: boolean;
  data: {
    recentActivities: JobActivityItem[];
    activityCounts: {
      total: number;
      byType: Record<string, number>;
    };
    lastActivityAt: string | null;
  };
  message: string;
}

export const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  description: 'Description',
  department: 'Department',
  location: 'Location',
  employment_type: 'Employment Type',
  seniority: 'Seniority',
  skills: 'Skills',
  tags: 'Tags',
  customer_rate: 'Customer Rate',
  engineer_rate: 'Engineer Rate',
  responsible_sales_id: 'Sales Owner',
  responsible_recruiter_id: 'Recruiter',
  open_for_recruiters: 'Open for Recruiters',
  customer_id: 'Customer'
};
